import React, { useCallback, useMemo, useRef } from 'react';
import { FaChevronDown } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Icon, IconButton,
  Popover, PopoverArrow, PopoverBody, PopoverCloseButton,
  PopoverContent, PopoverTrigger, Portal,
  Table, Tbody, Text, Th, Thead, Tr, useDisclosure
} from '@chakra-ui/react';
import PropTypes from 'prop-types';

import Authorizer from '../../../../../components/Authorizer';
import { CREATE_ITEM_CODE_STATUS_APPLIED, CREATE_ITEM_CODE_STATUS_WAIVED } from '../../../../../constants/permissions';
import { selectCompanyCodes } from '../../../../../redux/modules/company';
import { updateItemCodeStatus } from '../../../../../redux/modules/items';
import { showModal } from '../../../../../redux/modules/modal';
import { createMap } from '../../../../../utils/arrayUtil';

import CommentModal from './CommentModal';
import Row from './Row';

function ItemCodesPopover({ codes, itemId }) {
  const focusRef = useRef();
  const dispatch = useDispatch();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const companyCodes = useSelector(selectCompanyCodes);

  const itemCodesMap = useMemo(() => createMap(codes, 'id'), [codes]);

  const onChange = useCallback((codeAction, codeId, letter) => {
    if (codeAction === 'waive' || codeAction === 'inquire') {
      dispatch(showModal(() => (
        <CommentModal
          codeAction={codeAction}
          codeId={codeId}
          itemId={itemId}
          letter={letter}
        />
      ), {
        size: 'lg',
        title: 'Waive/Inquire Comment: '
      }));
    } else {
      dispatch(updateItemCodeStatus(itemId, codeId, codeAction, letter));
    }
  }, [dispatch, itemId]);

  return (
    <Popover
      initialFocusRef={focusRef}
      isOpen={isOpen}
      maxW="250px"
      placement="right"
      onClose={onClose}
      onOpen={onOpen}
    >
      <PopoverTrigger>
        <IconButton
          icon={<Icon as={FaChevronDown} />}
          variant="unstyled"
        />
      </PopoverTrigger>
      <Portal>
        <PopoverContent _focus={{ boxShadow: 'none' }} p={0}>
          <PopoverArrow bg="white" pr={0} />
          <PopoverCloseButton p={0} />
          <PopoverBody p={0} textTransform="uppercase">
            <Box fontSize="md" maxH="500px" overflowY="auto">
              <Table>
                <Thead borderColor="greyScale.200" borderWidth="0 0 2px 0">
                  <Tr>
                    <Th><Text flex={1} pt="26px">Codes</Text></Th>
                    <Authorizer permission={CREATE_ITEM_CODE_STATUS_APPLIED}>
                      <Th bgColor="greyScale.100"><Text p={2} pt="26px">Audit</Text></Th>
                    </Authorizer>
                    <Authorizer permission={CREATE_ITEM_CODE_STATUS_WAIVED}>
                      <Th><Text flex={1} pt="26px">Waive / X</Text></Th>
                    </Authorizer>
                  </Tr>
                </Thead>
                <Tbody marginTop="56px">
                  {companyCodes.map(code => (
                    <Row
                      code={code}
                      itemCode={itemCodesMap[code.id]}
                      key={code.id}
                      onChange={onChange}
                    />
                  ))}
                </Tbody>
              </Table>
            </Box>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
}

ItemCodesPopover.propTypes = {
  itemId: PropTypes.number,
  codes: PropTypes.arrayOf(PropTypes.shape())
};

export default ItemCodesPopover;
